import React, { useEffect, useState } from "react";
import api from "../../services/api";

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const minutes = value => {
  const total = Number(value || 0);

  if (total <= 0) return "-";
  if (total < 60) return `${total.toFixed(1)} min`;

  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);

  return `${h}h ${m}min`;
};

const todayBR = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const firstDayMonthBR = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

const formatDateTime = value => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return value;
  }
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const miniLabel = {
  color: "#64748b",
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 6
};

const miniValue = {
  color: "#0f172a",
  fontSize: 24,
  fontWeight: 900
};

const SellerAuditDashboard = () => {
  const [initialDate, setInitialDate] = useState(firstDayMonthBR());
  const [finalDate, setFinalDate] = useState(todayBR());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    summary: {},
    sellers: [],
    lastClosedTickets: []
  });

  const summary = data.summary || {};
  const sellers = data.sellers || [];
  const lastClosedTickets = data.lastClosedTickets || [];
  const criticalTickets = data.criticalTickets || [];

  const loadData = async () => {
    try {
      setLoading(true);

      const response = await api.get(
        `/dashboard/seller-audit?initialDate=${initialDate}&finalDate=${finalDate}`
      );

      setData(response.data || {});
    } catch (error) {
      console.error("Erro ao carregar auditoria dos vendedores:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #991b1b 55%, #dc2626 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#ffffff",
          boxShadow: "0 20px 45px rgba(220, 38, 38, 0.22)",
          marginBottom: 18
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 600 }}>
              Auditoria Comercial
            </div>
            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Auditoria dos Vendedores
            </h2>
            <div style={{ opacity: 0.85 }}>
              Vendas, mensagens enviadas, primeira resposta, leads sem resposta e produtividade individual.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Data inicial</div>
              <input
                type="date"
                value={initialDate}
                onChange={e => setInitialDate(e.target.value)}
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Data final</div>
              <input
                type="date"
                value={finalDate}
                onChange={e => setFinalDate(e.target.value)}
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}
              />
            </div>

            <button
              onClick={loadData}
              disabled={loading}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: "pointer",
                background: "#ffffff",
                color: "#b91c1c"
              }}
            >
              {loading ? "Carregando..." : "Filtrar"}
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 14,
          marginBottom: 16
        }}
      >
        <div style={cardStyle}>
          <div style={miniLabel}>Valor vendido</div>
          <div style={{ ...miniValue, color: "#16a34a" }}>{money(summary.salesAmount)}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Vendas</div>
          <div style={miniValue}>{summary.salesCount || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Conversão</div>
          <div style={{ ...miniValue, color: "#dc2626" }}>{percent(summary.conversionRate)}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Ticket médio</div>
          <div style={miniValue}>{money(summary.averageTicket)}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Mensagens enviadas</div>
          <div style={{ ...miniValue, color: "#2563eb" }}>{summary.messagesSent || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Tickets respondidos</div>
          <div style={{ ...miniValue, color: "#16a34a" }}>{summary.answeredTickets || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Leads sem resposta</div>
          <div style={{ ...miniValue, color: "#ef4444" }}>{summary.unansweredTickets || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>1ª resposta média</div>
          <div style={{ ...miniValue, color: "#f59e0b" }}>{minutes(summary.avgFirstResponseMinutes)}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Tempo médio até fechar</div>
          <div style={{ ...miniValue, color: "#7c3aed" }}>{minutes(summary.avgCloseMinutes)}</div>
        </div>

        <div style={cardStyle}>
          <div style={miniLabel}>Leads parados</div>
          <div style={{ ...miniValue, color: "#ef4444" }}>{summary.stoppedTickets || 0}</div>
        </div>
      </div>


      <div
        style={{
          ...cardStyle,
          marginBottom: 16,
          border: "1px solid #fecaca",
          background: "#fff7f7"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div>
            <div style={{ color: "#b91c1c", fontSize: 13, fontWeight: 800 }}>
              Leads críticos e parados
            </div>
            <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900 }}>
              Atendimentos abertos ou pendentes há mais de 30 minutos
            </div>
          </div>

          <div style={{ color: "#ef4444", fontWeight: 900, fontSize: 24 }}>
            {criticalTickets.length}
          </div>
        </div>

        {criticalTickets.length === 0 ? (
          <div style={{ color: "#16a34a", fontWeight: 800 }}>
            Nenhum lead crítico parado no momento.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                  <th style={{ padding: "10px 8px" }}>Ticket</th>
                  <th style={{ padding: "10px 8px" }}>Contato</th>
                  <th style={{ padding: "10px 8px" }}>Vendedor</th>
                  <th style={{ padding: "10px 8px" }}>Status</th>
                  <th style={{ padding: "10px 8px" }}>Parado há</th>
                  <th style={{ padding: "10px 8px" }}>Última atualização</th>
                  <th style={{ padding: "10px 8px" }}>Ação</th>
                </tr>
              </thead>

              <tbody>
                {criticalTickets.map(item => (
                  <tr key={item.id} style={{ borderTop: "1px solid #fecaca" }}>
                    <td style={{ padding: "12px 8px", fontWeight: 900 }}>#{item.id}</td>
                    <td style={{ padding: "12px 8px", fontWeight: 800 }}>{item.contactName || "Contato"}</td>
                    <td style={{ padding: "12px 8px" }}>{item.sellerName || "Sem vendedor"}</td>
                    <td style={{ padding: "12px 8px", color: "#f59e0b", fontWeight: 900 }}>{item.status}</td>
                    <td style={{ padding: "12px 8px", color: "#ef4444", fontWeight: 900 }}>
                      {minutes(item.minutesStopped)}
                    </td>
                    <td style={{ padding: "12px 8px" }}>{formatDateTime(item.updatedAt)}</td>
                    <td style={{ padding: "12px 8px" }}>
                      <button
                        onClick={() => item.uuid && (window.location.href = `/tickets/${item.uuid}`)}
                        disabled={!item.uuid}
                        style={{
                          border: 0,
                          borderRadius: 10,
                          padding: "8px 12px",
                          background: "#b91c1c",
                          color: "#fff",
                          fontWeight: 800,
                          cursor: item.uuid ? "pointer" : "not-allowed"
                        }}
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 16
        }}
      >
        <div style={cardStyle}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
              Ranking e produtividade por vendedor
            </div>
            <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900 }}>
              Performance individual no período
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1150 }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                  <th style={{ padding: "10px 8px" }}>#</th>
                  <th style={{ padding: "10px 8px" }}>Vendedor</th>
                  <th style={{ padding: "10px 8px" }}>Finalizados</th>
                  <th style={{ padding: "10px 8px" }}>Vendas</th>
                  <th style={{ padding: "10px 8px" }}>Valor</th>
                  <th style={{ padding: "10px 8px" }}>Conversão</th>
                  <th style={{ padding: "10px 8px" }}>Msgs</th>
                  <th style={{ padding: "10px 8px" }}>Respondidos</th>
                  <th style={{ padding: "10px 8px" }}>Sem resposta</th>
                  <th style={{ padding: "10px 8px" }}>1ª resposta</th>
                  <th style={{ padding: "10px 8px" }}>Tempo fechar</th>
                  <th style={{ padding: "10px 8px" }}>Parados</th>
                </tr>
              </thead>

              <tbody>
                {sellers.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ padding: 16, color: "#64748b" }}>
                      Nenhum dado encontrado para o período.
                    </td>
                  </tr>
                ) : (
                  sellers.map((seller, index) => (
                    <tr key={seller.userId || index} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "12px 8px", fontWeight: 900 }}>
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : index + 1}
                      </td>

                      <td style={{ padding: "12px 8px", fontWeight: 800, color: "#0f172a" }}>
                        {seller.name}
                      </td>

                      <td style={{ padding: "12px 8px" }}>{seller.closedTickets || 0}</td>
                      <td style={{ padding: "12px 8px", fontWeight: 800 }}>{seller.salesCount || 0}</td>

                      <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 900 }}>
                        {money(seller.salesAmount)}
                      </td>

                      <td style={{ padding: "12px 8px", color: "#dc2626", fontWeight: 900 }}>
                        {percent(seller.conversionRate)}
                      </td>

                      <td style={{ padding: "12px 8px", color: "#2563eb", fontWeight: 900 }}>
                        {seller.messagesSent || 0}
                      </td>

                      <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 900 }}>
                        {seller.answeredTickets || 0}
                      </td>

                      <td style={{ padding: "12px 8px", color: Number(seller.unansweredTickets || 0) > 0 ? "#ef4444" : "#16a34a", fontWeight: 900 }}>
                        {seller.unansweredTickets || 0}
                      </td>

                      <td style={{ padding: "12px 8px", color: "#f59e0b", fontWeight: 900 }}>
                        {minutes(seller.avgFirstResponseMinutes)}
                      </td>

                      <td style={{ padding: "12px 8px", color: "#7c3aed", fontWeight: 900 }}>
                        {minutes(seller.avgCloseMinutes)}
                      </td>

                      <td style={{ padding: "12px 8px", color: Number(seller.stoppedTickets || 0) > 0 ? "#ef4444" : "#16a34a", fontWeight: 900 }}>
                        {seller.stoppedTickets || 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
            Últimas finalizações
          </div>
          <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 12 }}>
            Atendimentos fechados
          </div>

          {lastClosedTickets.length === 0 ? (
            <div style={{ color: "#64748b" }}>Nenhuma finalização no período.</div>
          ) : (
            lastClosedTickets.slice(0, 12).map(item => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 10,
                  marginBottom: 10,
                  background: item.finalizadoComVenda ? "#f0fdf4" : "#fff"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <strong style={{ color: "#0f172a" }}>
                    #{item.id} - {item.contactName || "Contato"}
                  </strong>

                  <span
                    style={{
                      color: item.finalizadoComVenda ? "#16a34a" : "#ef4444",
                      fontWeight: 900
                    }}
                  >
                    {item.finalizadoComVenda ? "Venda" : "Sem venda"}
                  </span>
                </div>

                <div style={{ color: "#475569", fontSize: 12, marginTop: 4 }}>
                  Vendedor: {item.sellerName || "-"}
                </div>

                <div style={{ color: "#475569", fontSize: 12 }}>
                  Data: {formatDateTime(item.updatedAt)}
                </div>

                <div style={{ color: "#475569", fontSize: 12 }}>
                  Mensagens: {item.messageCount || 0}
                </div>

                {item.finalizadoComVenda ? (
                  <div style={{ color: "#16a34a", fontWeight: 900, marginTop: 4 }}>
                    {money(item.valorVenda)}
                  </div>
                ) : (
                  <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>
                    {item.motivoNaoVenda || "Sem motivo informado"}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerAuditDashboard;
